<template>
    <!-- Modal avec Teleport pour full screen -->
    <Teleport to="body">
        <div v-if="showModal" class="fixed inset-0 bg-[#0000009e] flex items-center justify-center z-50" @click="closeModal">
            <div
                class="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-all duration-300 ease-in-out" @click.stop>
                <div class="flex justify-between items-center mb-4">
                    <h2 class="text-2xl font-bold">
                        {{ modalTitle }}
                    </h2>
                    <button class="text-gray-500 hover:text-gray-700" @click="closeModal">
                        <XIcon class="h-6 w-6" />
                    </button>
                </div>

                <!-- Contenu du formulaire -->
                <div class="space-y-4">
                    <!-- Champs principaux -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Appellation *</label>
                            <input v-model="digitalEntityData.appellation"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Nom de l'entité numérique" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Type</label>
                            <input v-model="digitalEntityData.type"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Document, Image, Vidéo, Audio..." />
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Format</label>
                            <input v-model="digitalEntityData.format"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="PDF, JPG, MP4, MP3..." />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Version</label>
                            <input v-model="digitalEntityData.version"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="1.0, v2.1..." />
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700">Description</label>
                        <textarea v-model="digitalEntityData.description" rows="3"
                            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Description de l'entité numérique..."></textarea>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700">URL</label>
                        <input v-model="digitalEntityData.url" type="url"
                            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="https://exemple.com/fichier" />
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Propriétaire</label>
                            <input v-model="digitalEntityData.owner"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Nom du propriétaire" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Lien</label>
                            <input v-model="digitalEntityData.de_link"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="https://..." />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">Spécifications fichier</label>
                            <input v-model="digitalEntityData.file_spec"
                                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Format, résolution, etc." />
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700">Copyright</label>
                        <input v-model="digitalEntityData.copyright"
                            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="© 2024 Auteur" />
                    </div>

                    <!-- Visibilité -->
                    <div class="flex items-center">
                        <input id="is_public" v-model="digitalEntityData.is_public" type="checkbox"
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                        <label for="is_public" class="ml-2 block text-sm text-gray-900">
                            Rendre cette entité publique
                        </label>
                    </div>
                </div>

                <div class="flex justify-between mt-6">
                    <div class="ml-auto">
                        <button class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                            :disabled="!isFormValid || isSubmitting" @click="handleSubmit">
                            <span v-if="isSubmitting">Enregistrement...</span>
                            <span v-else>{{ submitButtonText }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>

    <!-- Modal de confirmation -->
    <ModalAreYouSure
      v-model="showConfirmModal"
      title="Modifications non sauvegardées"
      message="Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir fermer sans sauvegarder ?"
      confirm-text="Fermer sans sauvegarder"
      cancel-text="Continuer l'édition"
      @confirm="confirmClose"
      @cancel="cancelClose"
    />

    <!-- Notification -->
    <Notification ref="notification" />
</template>

<script lang="ts">
import { XIcon } from 'lucide-vue-next';
import type { ComputedRef, Ref } from 'vue';
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import { createDigitalEntity, updateDigitalEntity } from '../../api/digitalEntities';
import type { DigitalEntity, DigitalEntityCreate } from '../../api/types/entities/digitalEntity/types';
import Notification from '../Notification.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

export default defineComponent({
    name: 'DigitalEntityCreator',
    components: {
        XIcon,
        Notification,
        ModalAreYouSure
    },
    props: {
        show: {
            type: Boolean,
            required: true
        },
        mode: {
            type: String as () => 'create' | 'edit' | 'duplicate',
            default: 'create'
        },
        initialData: {
            type: Object as () => DigitalEntity | undefined,
            default: undefined
        },
        userId: {
            type: String,
            required: true
        },
        meAppellation: {
            type: String,
            required: false,
            default: ''
        },
        meIdentifier: {
            type: String,
            required: false,
            default: ''
        },
        creationOnlyMode: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: ['update:show', 'digital-entity-created', 'digital-entity-updated'],
    setup(props, { emit }) {
        const showModal: Ref<boolean> = ref(props.show);
        const isSubmitting: Ref<boolean> = ref(false);
        const notification = ref<InstanceType<typeof Notification> | null>(null);
        const showConfirmModal = ref(false);
        const initialDataSnapshot = ref<DigitalEntityCreate | null>(null);

        // Données du formulaire
        const digitalEntityData = reactive<DigitalEntityCreate>({
            user_id: props.userId,
            appellation: '',
            description: '',
            type: '',
            format: '',
            url: '',
            version: '',
            copyright: '',
            de_link: '',
            file_spec: '',
            owner: '',
            user: '',
            is_public: false
        });

        // Computed properties
        const modalTitle: ComputedRef<string> = computed((): string => {
            switch (props.mode) {
                case 'edit':
                    return 'Modifier l\'entité numérique';
                case 'duplicate':
                    return 'Dupliquer l\'entité numérique';
                default:
                    return 'Créer une nouvelle entité numérique';
            }
        });

        const submitButtonText: ComputedRef<string> = computed((): string => {
            switch (props.mode) {
                case 'edit':
                    return 'Mettre à jour';
                case 'duplicate':
                    return 'Dupliquer';
                default:
                    return 'Créer';
            }
        });

        const isFormValid: ComputedRef<boolean> = computed((): boolean => {
            return digitalEntityData.appellation.trim().length > 0;
        });

        // Watchers
        watch(() => props.show, (newValue: boolean): void => {
            showModal.value = newValue;
            if (newValue) {
                // Réinitialiser le formulaire quand le modal s'ouvre
                resetForm();
                // Si on a des données initiales, les charger
                if (props.initialData && typeof props.initialData === 'object' && 'uuid' in props.initialData) {
                    loadInitialData();
                }
                // Sauvegarder un snapshot des données initiales pour la comparaison
                if (props.initialData) {
                    initialDataSnapshot.value = JSON.parse(JSON.stringify(digitalEntityData));
                }
            } else {
                // Réinitialiser le snapshot quand le modal se ferme
                initialDataSnapshot.value = null;
            }
        });

        // Supprimer les autres watchers qui causent des conflits
        // watch(() => props.initialData, (): void => {
        //     if (props.show && props.initialData) {
        //         loadInitialData();
        //     }
        // });

        // watch([
        //     () => props.initialData,
        //     showModal
        // ], ([newData, modalVisible]) => {
        //     if (modalVisible && newData && typeof newData === 'object' && 'uuid' in newData) {
        //         console.log('[DigitalEntityCreator] Pré-remplissage avec initialData:', JSON.parse(JSON.stringify(newData)));
        //         Object.assign(digitalEntityData, {
        //             user_id: newData.user_id || props.userId,
        //             // 🚨 CORRECTION : Ne jamais copier l'UUID en mode duplicate ou create
        //             // uuid: newData.uuid || undefined, // SUPPRIMÉ - cause le conflit
        //             appellation: newData.appellation || '',
        //             description: newData.description || '',
        //             type: newData.type || '',
        //             format: newData.format || '',
        //             url: newData.url || '',
        //             version: newData.version || '',
        //             copyright: newData.copyright || '',
        //             de_link: newData.de_link || '',
        //             file_spec: newData.file_spec || '',
        //             owner: newData.owner || '',
        //             user: newData.user || '',
        //             is_public: newData.is_public ?? true
        //         });
        //         console.log('[DigitalEntityCreator] Formulaire après assignation :', JSON.parse(JSON.stringify(digitalEntityData)));
        //     } else {
        //         console.log('[DigitalEntityCreator] Watcher déclenché mais pas de données à appliquer (modalVisible =', modalVisible, ', initialData =', newData, ')');
        //     }
        // }, { immediate: true });

        // Méthodes
        const resetForm = (): void => {
            // Pré-remplissage automatique avec les données de l'objet d'étude pour tous les modes
            let autoAppellation = '';
            if (props.meAppellation || props.meIdentifier) {
                if (props.meAppellation && props.meIdentifier) {
                    autoAppellation = `${props.meAppellation} - ${props.meIdentifier}`;
                } else if (props.meAppellation) {
                    autoAppellation = props.meAppellation;
                } else if (props.meIdentifier) {
                    autoAppellation = props.meIdentifier;
                }
            }

            Object.assign(digitalEntityData, {
                user_id: props.userId,
                appellation: autoAppellation,
                description: '',
                type: '',
                format: '',
                url: '',
                version: '',
                copyright: '',
                de_link: '',
                file_spec: '',
                owner: '',
                user: '',
                is_public: true
            });
        };

        const loadInitialData = (): void => {
            if (!props.initialData) return;

            // Pour la duplication ET la création, utiliser meAppellation + meIdentifier pour l'appellation
            let appellationValue = props.initialData.appellation || '';
            if ((props.mode === 'duplicate' || props.mode === 'create') && (props.meAppellation || props.meIdentifier)) {
                if (props.meAppellation && props.meIdentifier) {
                    appellationValue = `${props.meAppellation} - ${props.meIdentifier}`;
                } else if (props.meAppellation) {
                    appellationValue = props.meAppellation;
                } else if (props.meIdentifier) {
                    appellationValue = props.meIdentifier;
                }
            }

            Object.assign(digitalEntityData, {
                user_id: props.userId,
                // 🚨 CORRECTION : Ne jamais copier l'UUID en mode duplicate
                // uuid: props.initialData.uuid, // SUPPRIMÉ - cause le conflit
                appellation: appellationValue,
                description: props.initialData.description || '',
                type: props.initialData.type || '',
                format: props.initialData.format || '',
                url: props.initialData.url || '',
                version: props.initialData.version || '',
                copyright: props.initialData.copyright || '',
                de_link: props.initialData.de_link || '',
                file_spec: props.initialData.file_spec || '',
                owner: props.initialData.owner || '',
                user: props.initialData.user || '',
                is_public: props.mode === 'duplicate' ? false : (props.initialData.is_public || false)
            });
        };

        // Fonction pour vérifier s'il y a des modifications
        const hasModifications = (): boolean => {
            if (!initialDataSnapshot.value) {
                // En mode création, vérifier si des champs ont été remplis
                return !!(digitalEntityData.appellation || digitalEntityData.description || digitalEntityData.type || 
                         digitalEntityData.format || digitalEntityData.url || digitalEntityData.version || 
                         digitalEntityData.copyright || digitalEntityData.de_link || digitalEntityData.file_spec || 
                         digitalEntityData.owner || digitalEntityData.user);
            }
            
            // En mode édition, comparer avec les données initiales
            const snapshot = initialDataSnapshot.value;
            return digitalEntityData.appellation !== snapshot.appellation ||
                   digitalEntityData.description !== snapshot.description ||
                   digitalEntityData.type !== snapshot.type ||
                   digitalEntityData.format !== snapshot.format ||
                   digitalEntityData.url !== snapshot.url ||
                   digitalEntityData.version !== snapshot.version ||
                   digitalEntityData.copyright !== snapshot.copyright ||
                   digitalEntityData.de_link !== snapshot.de_link ||
                   digitalEntityData.file_spec !== snapshot.file_spec ||
                   digitalEntityData.owner !== snapshot.owner ||
                   digitalEntityData.user !== snapshot.user ||
                   digitalEntityData.is_public !== snapshot.is_public;
        };

        const closeModal = (): void => {
            if (hasModifications()) {
                showConfirmModal.value = true;
            } else {
                showModal.value = false;
                emit('update:show', false);
            }
        };

        const confirmClose = (): void => {
            showConfirmModal.value = false;
            showModal.value = false;
            emit('update:show', false);
        };

        const cancelClose = (): void => {
            showConfirmModal.value = false;
        };

        const handleSubmit = async (): Promise<void> => {
            if (!isFormValid.value || isSubmitting.value) return;

            isSubmitting.value = true;

            try {
                if (props.mode === 'edit' && props.initialData) {
                    // Mode édition
                    const updatedEntity = await updateDigitalEntity(props.initialData.uuid, digitalEntityData);
                    emit('digital-entity-updated', updatedEntity);
                    if (notification.value) {
                        notification.value.showNotification('Entité numérique mise à jour avec succès', 'success');
                    }
                    resetForm();
                    showModal.value = false;
                    emit('update:show', false);
                } else {
                    // Mode création ou duplication
                    const newEntity = await createDigitalEntity(digitalEntityData);
                    emit('digital-entity-created', newEntity);
                    if (notification.value) {
                        notification.value.showNotification('Entité numérique créée avec succès', 'success');
                    }
                    resetForm();
                    showModal.value = false;
                    emit('update:show', false);
                }
            } catch (error) {
                console.error('Erreur lors de la sauvegarde de l\'entité numérique:', error);
                if (notification.value) {
                    notification.value.showNotification('Erreur lors de la sauvegarde de l\'entité numérique', 'error');
                }
            } finally {
                isSubmitting.value = false;
            }
        };

        return {
            showModal,
            isSubmitting,
            digitalEntityData,
            modalTitle,
            submitButtonText,
            isFormValid,
            closeModal,
            handleSubmit,
            notification,
            showConfirmModal,
            confirmClose,
            cancelClose
        };
    }
});
</script>

<style scoped>
/* Forcer les styles pour tous les inputs dans DigitalEntityCreator */
input[type="text"],
input[type="email"],
input[type="url"],
input[type="password"],
input[type="number"],
input:not([type]),
textarea,
select {
    margin-bottom: 8px !important;
    border: 1px solid #d1d5db !important;
    /* border-gray-300 */
    border-radius: 6px !important;
    /* rounded-md */
    padding: 8px 12px !important;
    /* px-3 py-2 */
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
    /* shadow-sm */
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
    background-color: white !important;
    width: 100% !important;
    display: block !important;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="url"]:focus,
input[type="password"]:focus,
input[type="number"]:focus,
input:not([type]):focus,
textarea:focus,
select:focus {
    outline: none !important;
    border-color: #3b82f6 !important;
    /* border-blue-500 */
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
    /* ring-blue-500 */
}

input::placeholder,
textarea::placeholder {
    color: #9ca3af !important;
    /* text-gray-400 */
}

/* Labels */
label {
    margin-bottom: 4px !important;
    display: block !important;
    font-weight: 500 !important;
    color: #374151 !important;
    /* text-gray-700 */
    font-size: 0.875rem !important;
    /* text-sm */
}

/* Checkbox spécifique */
input[type="checkbox"] {
    width: auto !important;
    display: inline-block !important;
    margin-right: 8px !important;
    margin-bottom: 0 !important;
}
</style>